/**
 * PaymentRequiredContext — cross-cutting `PAYMENT_REQUIRED` signal holder.
 *
 * signup-billing-mvp Phase 6 Session 3 (2026-05-13).
 *
 * The Phase-2 streamMessage eligibility gate fires an SSE `error` frame with
 * `code: 'PAYMENT_REQUIRED'` when a user is blocked OR runs out of monthly
 * allowance. The `<MonthlyAllowanceBanner>` (mounted in the layout shell at
 * `__root.tsx`) needs to react to that signal. `useStreaming` (called from
 * Builder + Solver + public-deal chat) needs to push it.
 *
 * This context is the shared holder. The producer wires via
 * `usePaymentRequiredContext().setPaymentRequired(...)`; the consumer reads
 * via the thinner `useStreamingPaymentRequired()` accessor (which closes the
 * setter into a `dismiss()` callback). State is intentionally in-memory only
 * — Pattern B per research § 11 risk #3: the next `sendMessage` call clears
 * stale state at run-start so the banner reappears on retry.
 *
 * Modeled on the existing `AuthProvider` pattern (`hooks/use-auth.tsx`).
 */

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  type FC,
  type ReactNode,
} from 'react';

export interface PaymentRequiredState {
  /**
   * Reason carried on the SSE error `details.reason` field — drives the
   * banner's user-facing copy (`'allowance_exhausted' | 'locked' |
   * 'no_subscription'` per Phase-2 streamMessage eligibility gate; the
   * banner consumes them generically and falls back to a default string for
   * unknown values).
   */
  reason: string;
}

interface PaymentRequiredContextValue {
  paymentRequired: PaymentRequiredState | null;
  setPaymentRequired: (next: PaymentRequiredState | null) => void;
}

const PaymentRequiredContext = createContext<PaymentRequiredContextValue | null>(null);

export const PaymentRequiredProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [paymentRequired, setPaymentRequiredState] = useState<PaymentRequiredState | null>(null);
  const setPaymentRequired = useCallback(
    (next: PaymentRequiredState | null) => setPaymentRequiredState(next),
    [],
  );
  const value = useMemo(
    () => ({ paymentRequired, setPaymentRequired }),
    [paymentRequired, setPaymentRequired],
  );
  return (
    <PaymentRequiredContext.Provider value={value}>{children}</PaymentRequiredContext.Provider>
  );
};

export function usePaymentRequiredContext(): PaymentRequiredContextValue {
  const ctx = useContext(PaymentRequiredContext);
  if (!ctx) {
    throw new Error(
      'usePaymentRequiredContext must be used within <PaymentRequiredProvider>',
    );
  }
  return ctx;
}
